import { useState, useEffect } from "react";
import { ApolloCache, gql, useApolloClient, useLazyQuery } from "@apollo/client";
import { Control, useController, FieldPathByValue, FieldValues } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import { Facility } from "@/types";
import { GET_FACILITIES_SEARCH_QUERY } from "../queries";

type NewEmploymentFacility = Pick<Facility, "id" | "name" | "mainAddress">;

const renderItem = (facility: NewEmploymentFacility) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-sys-brand-surface-container-high flex items-center justify-center">
      <Icon name="location_on" className="text-gray-500" />
    </div>
    <div className="flex flex-col">
      <span>{facility.name}</span>
      {facility.mainAddress && (
        <span className="text-body-small text-gray-500">
          {facility.mainAddress.address1}, {facility.mainAddress.city}, {facility.mainAddress.state}
        </span>
      )}
    </div>
  </div>
);

function loadFacilityName(cache: ApolloCache<object>, id: string) {
  const data = cache.readFragment<{ name: string }>({
    fragment: gql`
      fragment FacilityName on BackofficeFacility {
        name
      }
    `,
    id: cache.identify({ __typename: "BackofficeFacility", id })
  });

  return data?.name;
}

type FacilityAutocompleteProps<T extends FieldValues> = {
  control: Control<T>;
  label: string;
  name: FieldPathByValue<T, string>;
  disabled?: boolean;
  initialDisplayName?: string;
  /** When false, empty facility is allowed (e.g. employer-only application rows). Defaults to true. */
  required?: boolean;
  /** When true, facility search returns active facilities only. Opt-in so career employment keeps prior search behavior. */
  excludeArchived?: boolean;
};

export function FacilityAutocomplete<T extends FieldValues>({
  control,
  label,
  name,
  disabled,
  initialDisplayName,
  required = true,
  excludeArchived = false
}: FacilityAutocompleteProps<T>) {
  const {
    field: { onChange, value },
    fieldState: { error }
  } = useController({
    control,
    name,
    rules: { required: required ? "Facility is required" : undefined }
  });

  const [facilities, setFacilities] = useState<NewEmploymentFacility[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [facilityName, setFacilityName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [facilitySearchQuery] = useLazyQuery(GET_FACILITIES_SEARCH_QUERY);
  const { cache } = useApolloClient();

  useEffect(() => {
    const searchFacilities = async () => {
      if (!searchQuery.trim()) {
        setFacilities([]);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await facilitySearchQuery({
          variables: {
            first: 10,
            input: {
              search: searchQuery,
              ...(excludeArchived ? { excludeArchived: true } : {})
            }
          }
        });
        setFacilities(data?.facilities?.nodes || []);
      } catch (error) {
        setFacilities([]);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchFacilities, 300); // 300ms debounce
    return () => clearTimeout(timeoutId);
  }, [searchQuery, facilitySearchQuery, excludeArchived]);

  useEffect(() => {
    if (value) {
      const name = loadFacilityName(cache, value);
      if (name) {
        setFacilityName(name);
      } else if (initialDisplayName) {
        setFacilityName(initialDisplayName);
      }
    }
  }, [value, cache, initialDisplayName]);

  useEffect(() => {
    if (!value) {
      setSearchQuery("");
      setFacilityName("");
    }
  }, [value]);

  return (
    <Autocomplete
      label={label}
      data={facilities}
      renderItem={renderItem}
      onSelect={(facility: NewEmploymentFacility) => {
        setSearchQuery("");
        onChange(facility.id);
      }}
      onChangeText={(text) => {
        if (facilityName.length) setFacilityName("");
        setSearchQuery(text);
        if (!text) onChange("");
      }}
      query={facilityName || searchQuery}
      leadingIcon="search"
      error={!!error}
      errorText={error?.message}
      loadingResults={isSearching}
      reset={() => {
        setSearchQuery("");
        setFacilityName("");
        onChange("");
      }}
      disabled={disabled}
    />
  );
}
